import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Alert,
  Box,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useApi } from '../services/api';

interface Template {
  id: string;
  name: string;
  vendor: string;
  switch_type: string;
  parameter_schema?: any;
  template_content?: string;
  supported_features?: Record<string, boolean>;
}

interface ManualConfigurationFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (configuration: any) => void;
  template: Template | null;
  vendor: string;
  switchType: string;
}

interface ParameterField {
  name: string;
  type: string;
  label: string;
  required: boolean;
  default?: any;
  description?: string;
  options?: string[];
  pattern?: string;
  min?: number;
  max?: number;
}

const ManualConfigurationForm: React.FC<ManualConfigurationFormProps> = ({
  open,
  onClose,
  onSave,
  template,
  vendor,
  switchType
}) => {
  const { getAuthenticatedApi } = useApi();
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [parameterFields, setParameterFields] = useState<ParameterField[]>([]);
  const [previewConfig, setPreviewConfig] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoPreview, setAutoPreview] = useState(true);

  useEffect(() => {
    if (template && template.parameter_schema) {
      parseParameterSchema(template.parameter_schema);
    } else {
      // Default parameters if no schema
      setParameterFields(getDefaultFields());
    }
  }, [template]);

  useEffect(() => {
    if (autoPreview && Object.keys(parameters).length > 0) {
      const timer = setTimeout(() => {
        generatePreview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [parameters, autoPreview]);

  const parseParameterSchema = (schema: any) => {
    const fields: ParameterField[] = [];
    const defaultParams: Record<string, any> = {};

    // Parse JSON schema or custom schema format
    if (schema.properties) {
      Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
        fields.push({
          name: key,
          type: prop.type || 'string',
          label: prop.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          required: schema.required?.includes(key) || false,
          default: prop.default,
          description: prop.description,
          options: prop.enum,
          pattern: prop.pattern,
          min: prop.minimum,
          max: prop.maximum
        });
        
        if (prop.default !== undefined) {
          defaultParams[key] = prop.default;
        }
      });
    } else {
      // Fallback to default fields
      fields.push(...getDefaultFields());
    }

    setParameterFields(fields);
    setParameters(defaultParams);
  };

  const getDefaultFields = (): ParameterField[] => {
    // Default fields based on vendor and switch type
    const baseFields: ParameterField[] = [
      {
        name: 'switch_name',
        type: 'string',
        label: 'Switch Name',
        required: true,
        description: 'Hostname for the switch',
        pattern: '^[A-Za-z0-9-_]+$'
      },
      {
        name: 'switch_ip',
        type: 'string',
        label: 'Management IP',
        required: true,
        description: 'Management IP address',
        pattern: '^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}$'
      },
      {
        name: 'switch_location',
        type: 'string',
        label: 'Location',
        required: false,
        description: 'Physical location of the switch'
      }
    ];

    // Add vendor-specific fields
    if (vendor === 'cisco') {
      baseFields.push({
        name: 'enable_secret',
        type: 'password',
        label: 'Enable Secret',
        required: true,
        description: 'Enable password for privileged mode'
      });
    }

    // Add switch type specific fields
    if (switchType === 'core') {
      baseFields.push({
        name: 'is_primary',
        type: 'boolean',
        label: 'Primary Core Switch',
        required: false,
        default: false,
        description: 'Is this the primary switch in VRRP/HSRP?'
      });
      baseFields.push({
        name: 'vrrp_priority',
        type: 'number',
        label: 'VRRP/HSRP Priority',
        required: false,
        default: 100,
        min: 1,
        max: 255,
        description: 'Priority for redundancy protocol'
      });
    }

    return baseFields;
  };

  const handleParameterChange = (name: string, value: any) => {
    setParameters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generatePreview = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const api = await getAuthenticatedApi();
      const result = await api.generateManualConfiguration({
        template_id: template?.id,
        vendor: vendor,
        switch_type: switchType,
        parameters: parameters,
        validate: true
      });

      setPreviewConfig(result.configuration_text || '');
      setValidationResult(result.validation);
    } catch (error: any) {
      setError(error.message || 'Failed to generate preview');
      setPreviewConfig('# Error generating configuration preview');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!previewConfig) {
      await generatePreview();
    }

    const configData = {
      template_id: template?.id,
      template_name: template?.name,
      vendor: vendor,
      switch_type: switchType,
      parameters: parameters,
      configuration: previewConfig,
      validation: validationResult,
      manual: true
    };

    onSave(configData);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(previewConfig);
  };

  const renderField = (field: ParameterField) => {
    const value = parameters[field.name] ?? field.default ?? '';

    switch (field.type) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === true}
                onChange={(e) => handleParameterChange(field.name, e.target.checked)}
              />
            }
            label={field.label}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.label}
            value={value}
            onChange={(e) => handleParameterChange(field.name, parseInt(e.target.value) || 0)}
            required={field.required}
            helperText={field.description}
            InputProps={{
              inputProps: {
                min: field.min,
                max: field.max
              }
            }}
          />
        );

      case 'password':
        return (
          <TextField
            fullWidth
            type="password"
            label={field.label}
            value={value}
            onChange={(e) => handleParameterChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.description}
          />
        );

      default:
        if (field.options) {
          return (
            <TextField
              select
              fullWidth
              label={field.label}
              value={value}
              onChange={(e) => handleParameterChange(field.name, e.target.value)}
              required={field.required}
              helperText={field.description}
            >
              {field.options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        return (
          <TextField
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleParameterChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.description}
            error={field.pattern && value && !new RegExp(field.pattern).test(value)}
          />
        );
    }
  };

  const getValidationIcon = () => {
    if (!validationResult) return null;
    
    if (!validationResult.is_valid) {
      return <ErrorIcon color="error" />;
    } else if (validationResult.warnings?.length > 0) {
      return <WarningIcon color="warning" />;
    } else {
      return <CheckCircleIcon color="success" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <BuildIcon color="primary" />
          <Typography variant="h6">Manual Configuration</Typography>
          {template && (
            <Chip 
              label={template.name} 
              color="primary" 
              variant="outlined"
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Parameters Form */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="medium">
                Configuration Parameters
              </Typography>
              
              {error && <Alert severity="error">{error}</Alert>}
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    {parameterFields.map(field => (
                      <Grid size={12} key={field.name}>
                        {renderField(field)}
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoPreview}
                      onChange={(e) => setAutoPreview(e.target.checked)}
                    />
                  }
                  label="Auto-preview"
                />
                {!autoPreview && (
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={generatePreview}
                    disabled={generating}
                  >
                    Generate Preview
                  </Button>
                )}
              </Stack>
            </Stack>
          </Grid>

          {/* Preview */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="medium">
                  Configuration Preview
                </Typography>
                <Stack direction="row" spacing={1}>
                  {getValidationIcon()}
                  <IconButton size="small" onClick={copyToClipboard} disabled={!previewConfig}>
                    <Tooltip title="Copy to clipboard">
                      <CopyIcon />
                    </Tooltip>
                  </IconButton>
                </Stack>
              </Stack>

              {generating && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Generating configuration...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              {validationResult && (
                <Alert 
                  severity={!validationResult.is_valid ? 'error' : 
                           validationResult.warnings?.length > 0 ? 'warning' : 'success'}
                >
                  {!validationResult.is_valid ? (
                    <>
                      <Typography variant="subtitle2">Validation Errors:</Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {validationResult.errors?.map((err: string, idx: number) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </>
                  ) : validationResult.warnings?.length > 0 ? (
                    <>
                      <Typography variant="subtitle2">Warnings:</Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {validationResult.warnings.map((warn: string, idx: number) => (
                          <li key={idx}>{warn}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    'Configuration is valid'
                  )}
                </Alert>
              )}

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Editor
                  height="500px"
                  defaultLanguage="shell"
                  value={previewConfig}
                  theme="vs-dark"
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 12
                  }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!previewConfig || generating || 
                   (validationResult && !validationResult.is_valid)}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualConfigurationForm;